import { useQuery } from '@tanstack/react-query';
import { useAuth, useUser, IS_DEMO_MODE } from '@/lib/auth';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Settings as SettingsIcon, Lock, Pencil } from 'lucide-react';
import { api } from '@/lib/api';
import { Section } from '@/components/Section';
import { Empty } from '@/components/Empty';
import { cn } from '@/lib/cn';

export function SettingsPage() {
  const { slug } = useParams<{ slug: string }>();
  const { getToken } = useAuth();
  const { user } = useUser();
  const isAdmin = user?.publicMetadata?.role === 'admin';

  const promptsQuery = useQuery({
    queryKey: ['prompts', slug],
    queryFn: () => api.listPrompts(slug!, () => getToken()),
  });

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold leading-tight">Réglages</h1>
        <p className="text-sm text-ducens-muted mt-1">Configuration des agents et des prompts.</p>
      </div>

      <Section
        title="Prompts actifs"
        description={isAdmin ? 'Templates utilisés par les agents IA. Cliquer pour éditer.' : 'Templates utilisés par les agents IA. Lecture seule.'}
      >
        {IS_DEMO_MODE && (
          <div className="bg-ducens-accent/30 border border-ducens-accent rounded-md p-3 text-sm text-ducens-ink mb-4 flex items-start gap-2">
            <Lock className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              Mode démo : tu peux ouvrir l'éditeur mais la sauvegarde est simulée.
            </div>
          </div>
        )}

        {promptsQuery.isPending && (
          <div className="flex items-center gap-2 text-ducens-muted">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement...
          </div>
        )}

        {promptsQuery.data?.length === 0 && (
          <Empty Icon={SettingsIcon} title="Aucun prompt enregistré" />
        )}

        {promptsQuery.data && promptsQuery.data.length > 0 && (
          <div className="space-y-2">
            {promptsQuery.data.map((p) => {
              const inner = (
                <>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <div className="font-medium font-mono text-sm flex items-center gap-2">
                        {p.name}
                        {isAdmin && <Pencil className="w-3 h-3 text-ducens-muted" />}
                      </div>
                      <div className="text-xs text-ducens-muted mt-0.5">{p.description}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <ModelBadge model={p.model_target} />
                      <span className="text-xs text-ducens-muted">v{p.version}</span>
                    </div>
                  </div>
                  <p className="text-sm text-ducens-muted leading-relaxed">{p.excerpt}</p>
                </>
              );

              if (isAdmin) {
                return (
                  <Link
                    key={p.name}
                    to={`/clients/${slug}/settings/prompts/${p.name}`}
                    className="block bg-white border border-ducens-border rounded-md p-4 hover:border-ducens-ink transition-colors"
                  >
                    {inner}
                  </Link>
                );
              }
              return (
                <div key={p.name} className="bg-white border border-ducens-border rounded-md p-4">
                  {inner}
                </div>
              );
            })}
          </div>
        )}
      </Section>

      <Section title="Modes" description="Comportement global du système">
        <div className="bg-white border border-ducens-border rounded-lg p-4 space-y-3">
          <SettingRow
            label="Mode auto-publication"
            value={IS_DEMO_MODE ? 'Désactivé (démo)' : 'À configurer'}
            description="Si activé, les outputs sociaux ne passent plus par la file d'approbation."
          />
          <SettingRow
            label="Budget Anthropic quotidien"
            value="6 $"
            description="Hard cap. Les agents non critiques sont pausés automatiquement à dépassement."
          />
          <SettingRow
            label="Plage horaire de publication"
            value="07h - 22h Europe/Paris"
            description="Les posts ne sont jamais publiés en dehors de cette plage."
          />
        </div>
      </Section>
    </>
  );
}

function ModelBadge({ model }: { model: string }) {
  const isHaiku = model.toLowerCase().includes('haiku');
  return (
    <span
      className={cn(
        'inline-flex px-2 py-0.5 rounded text-[10px] font-medium font-mono',
        isHaiku ? 'bg-emerald-100 text-emerald-900' : 'bg-purple-100 text-purple-900'
      )}
    >
      {isHaiku ? 'haiku' : 'sonnet'}
    </span>
  );
}

function SettingRow({ label, value, description }: { label: string; value: string; description: string }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-ducens-muted mt-0.5">{description}</div>
      </div>
      <div className="text-sm font-mono shrink-0 text-ducens-muted">{value}</div>
    </div>
  );
}
